import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import type { ClassificationType } from '@/views/classification/classificationTypes';
import { formatMegabytes } from '@/utils/files';
import {
  ConnectedByV1Dto,
  type DocumentFilterInnerV2Dto,
  DocumentsService,
  type DocumentStampedV1Dto,
  type DocumentType,
  type DocumentV1Dto,
  type PatchDocumentStampedV1Dto,
  type PatchDocumentV1Dto,
  type SimpleConnectV1Dto,
  TicketDocumentV1Dto,
  type UploadDocumentRequestV1,
  type UploadManyDocumentsRequestV1,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { DocumentSimple } from '../documentsTypes';

export function useDocuments() {
  const fetchDocument = async (id: string) => {
    try {
      return await DocumentsService.documentControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchDocuments = async ({
    orderBy,
    page,
    limit,
    filter,
    searchString,
    documentType,
    isClassified,
    ticketId,
  }: FetchQueryParams<DocumentFilterInnerV2Dto> & {
    documentType?: DocumentType;
    isClassified?: boolean;
    ticketId?: string;
  }) => {
    try {
      return DocumentsService.documentControllerV2FindAllV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
        documentType,
        isClassified,
        ticketId,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchDocumentsSimple = async ({
    orderBy,
    page,
    limit,
    filter,
    searchString,
    documentType,
    isClassified,
    ticketId,
  }: FetchQueryParams<DocumentFilterInnerV2Dto> & {
    documentType?: DocumentType;
    isClassified?: boolean;
    ticketId?: string;
  }) => {
    try {
      return DocumentsService.documentControllerV2FindAllSimpleV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
        documentType,
        isClassified,
        ticketId,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const uploadDocument = async (data: UploadDocumentRequestV1) => {
    try {
      const response = await DocumentsService.documentControllerV1UploadOneDocumentV1(data);
      eventBus.emit('upload');
      eventBus.emit('documents:uploaded', Array.isArray(response) ? response : [response]);
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const uploadDocuments = async (data: UploadManyDocumentsRequestV1) => {
    try {
      const response = await DocumentsService.documentControllerV1UploadManyDocumentsV1(data);
      eventBus.emit('upload');
      eventBus.emit('documents:uploaded', Array.isArray(response) ? response : [response]);
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateDocument = async (id: string, data: PatchDocumentV1Dto) => {
    try {
      const response = await DocumentsService.documentControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateDocumentStamp = async (id: string, data: PatchDocumentStampedV1Dto) => {
    try {
      const response = await DocumentsService.documentControllerV1UpdatePartialDocumentStampV1(
        id,
        data,
      );
      eventBus.emit('update');
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateDocumentStampFinal = async (id: string, data: PatchDocumentStampedV1Dto) => {
    try {
      const response = await DocumentsService.documentControllerV1UpdatePartialDocumentFinalStampV1(
        id,
        data,
      );
      eventBus.emit('update');
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const downloadDocument = async (id: string) => {
    try {
      await DocumentsService.documentControllerV1DownloadDocumentV1(id);
      eventBus.emit('download');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      await DocumentsService.documentControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const batchDeleteDocuments = async (ids: string[]) => {
    try {
      return await DocumentsService.documentControllerV1RemoveManyV1({
        values: ids,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const deleteDocumentRelation = async (uuid: string, ticketUuid: string) => {
    try {
      return await DocumentsService.documentControllerV1RemoveTicketRelationV1(uuid, ticketUuid);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return {
    fetchDocument,
    fetchDocuments,
    fetchDocumentsSimple,
    uploadDocument,
    uploadDocuments,
    updateDocument,
    updateDocumentStamp,
    updateDocumentStampFinal,
    downloadDocument,
    deleteDocument,
    batchDeleteDocuments,
    deleteDocumentRelation,
  };
}

function getDocumentStampedProps(documentStamped: DocumentStampedV1Dto) {
  const {
    id: _id,
    createdAt: _createdAt,
    modifiedAt: _modifiedAt,
    documentId: _documentId,
    ...rest
  } = documentStamped;

  return rest;
}

export function useDocumentsConverter() {
  function extractClassifications(data: DocumentSimple | undefined): ClassificationType[] {
    if (!data) return [{ pointOfSaleId: undefined, documentType: undefined, ticketId: undefined }];

    const classifications: ClassificationType[] = [];
    const validTickets = data.tickets.filter(Boolean);
    if (validTickets.length > 0) {
      // Iterate through each ticket
      validTickets.forEach((ticketId: string) => {
        // Find the pointOfSale that has this ticket in its connectedBy array
        const pos = data.pointOfSales.find((p: SimpleConnectV1Dto) =>
          p.connectedBy?.some(
            (conn: ConnectedByV1Dto) =>
              conn.id === ticketId &&
              conn.connectionType === ConnectedByV1Dto.connectionType.TICKET,
          ),
        );

        // Find the documentType that has this ticket in its connectedBy array
        const documentType = data.documentTypes.find((d: SimpleConnectV1Dto) =>
          d.connectedBy?.some(
            (conn: ConnectedByV1Dto) =>
              conn.id === ticketId &&
              conn.connectionType === ConnectedByV1Dto.connectionType.TICKET,
          ),
        );

        // Create a classification entry for this ticket
        classifications.push({
          pointOfSaleId: pos?.id,
          documentType: documentType?.name as TicketDocumentV1Dto.documentType,
          ticketId: ticketId,
        });
      });
    } else if (data.pointOfSaleId && data.documentType) {
      classifications.push({
        pointOfSaleId: data.pointOfSaleId ?? undefined,
        documentType: data.documentType ?? undefined,
        ticketId: data.ticketId ?? undefined,
      });
    }

    // If we found classifications, return them
    if (classifications.length > 0) {
      return classifications;
    }

    // Default empty classification
    return [{ pointOfSaleId: undefined, documentType: undefined, ticketId: undefined }];
  }

  const convertDocumentDtoToDocumentSimple = (data: DocumentV1Dto): DocumentSimple => {
    const {
      id,
      createdAt,
      modifiedAt,
      version,
      name,
      size,
      mimeType,
      stamped,
      comment,
      documentId,
      pointOfSales,
      documentTypes,
      ticketToDocuments,
      ticketToAttachments,
      documentStamped,
    } = data;

    const result = {
      id,
      createdAt,
      modifiedAt,
      version,
      name,
      documentId,
      size: formatMegabytes(size),
      mimeType,
      stamped: stamped ?? false,
      comment,
      pointOfSales: pointOfSales ?? [],
      tickets: Array.from(
        new Set([
          ...(ticketToDocuments?.map((x) => x.id) ?? []),
          ...(ticketToAttachments?.map((x) => x.id) ?? []),
        ]),
      ),
      documentTypes: documentTypes ?? [],
      ...(documentStamped ? getDocumentStampedProps(documentStamped) : {}),
    };

    return {
      ...result,
      documentType: result.documentTypes?.[0]
        ? (result.documentTypes?.[0].name as TicketDocumentV1Dto.documentType)
        : undefined,
      ticketId: result.tickets?.[0] ? result.tickets?.[0] : undefined,
      pointOfSaleId: result.pointOfSales?.[0] ? result.pointOfSales?.[0].id : undefined,
      isAttachment: !!ticketToAttachments?.length,
    };
  };

  const convertDocumentListDtoToDocumentSimpleList = (data: DocumentV1Dto[]): DocumentSimple[] => {
    return data.map(convertDocumentDtoToDocumentSimple);
  };

  const convertDocumentSimpleToUpdateDocumentDto = (data: DocumentSimple): PatchDocumentV1Dto => {
    const {
      id: _id,
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      mimeType: _mimeType,
      size: _size,
      stamped: _stamped,
      documentId: _documentId,
      ticketNumber: _ticketNumber,
      costCenter: _costCenter,
      generalLeaderAccount: _generalLeaderAccount,
      generalLeaderAccountNr: _generalLeaderAccountNr,
      oneTimeContribution: _oneTimeContribution,
      approvalNr: _approvalNr,
      amount: _amount,
      interval: _interval,
      reviewer1Name: _reviewer1Name,
      reviewer1Date: _reviewer1Date,
      reviewer2Name: _reviewer2Name,
      reviewer2Date: _reviewer2Date,
      currency: _currency,
      documentTypes: _documentTypes, //TODO handle document type update in case of classification
      tickets: _tickets, //TODO handle tickets update in case of classification
      pointOfSales: _pointOfSales, //TODO handle pointOfSales update in case of classification
      ...res
    } = data;
    return {
      ...res,
      ticketId: data.ticketId ?? undefined,
    };
  };

  return {
    extractClassifications,
    convertDocumentDtoToDocumentSimple,
    convertDocumentListDtoToDocumentSimpleList,
    convertDocumentSimpleToUpdateDocumentDto,
  };
}
